import { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportDownloadDetail } from '../interfaces/ReportDownloadDetail';
import useNswagClient from './../../../hooks/api/useNswagClient';
import { prepareReports } from '../helpers/ReportingHelper';
import LoadingWheel from './../../../components/ui/LoadingWheel';
import { Autocomplete, Box, Button, FormControl, SvgIcon, TextField, Typography } from '@mui/material';
import { BookClosed, DownloadCloud01 } from './../../../assets';
import CustomPagination from './../../../components/forms/Pagination';
import { theme } from '../../../theme';
import { User } from './../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';

const UserReportsTab = () => {
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [reports, setReports] = useState<ReportDownloadDetail[]>([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [downloadReportId, setDownloadReportId] = useState(0);
  const { getStoreUsersWithRole, getUserReports, getUserReport } = useNswagClient();

  useEffect(() => {
    setSelectedUser(null);
    setReports([]);
    setPage(0);

    getUsers();
  }, [selectedStore]);

  const getUsers = () => {
    if (!selectedStore?.storeNumber) return;

    setIsLoading(true);
    getStoreUsersWithRole(26, selectedStore.storeNumber).then((response) => {
      if (response?.data) setAllUsers(response.data);
    }).catch((error) => {
      console.log(error);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const getReports = (userId: number) => {
    setIsLoading(true);
    getUserReports(userId).then((response) => {
      if (response?.data?.reports) {
        const reportDetails = prepareReports(response.data.reports);
    
        setReports(reportDetails);
        setPage(0);
      }
    }).catch((error) => {
      console.log(error);
    }).finally(() => {
      setIsLoading(false);
    });
  };
    
  const maxPages = () => {
    return Math.ceil(allUsers.length / rowsPerPage);
  };
    
  const visibleRows = () => {
    return reports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };
    
  const downloadReport = (name: string, id: number) => {
    if (!selectedUser?.id) return;

    setDownloadReportId(id);
    getUserReport(selectedUser.id, id).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
    
      const fileName = `${name}.xlsx`;
    
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    }).catch((error) => {
      console.log(error);
    }).finally(() => {
      setDownloadReportId(0);
    });
  };

  const handleSelectChange = (event: SyntheticEvent, value: User | null) => {
    setSelectedUser(value);

    if (!value?.id) {
      setReports([]);
      return;
    }
    getReports(value.id);
  };

  return (
    <>
      {
        isLoading ?
          <LoadingWheel /> :
          <>
            <FormControl fullWidth
              sx={{ pb: '12px' }}>
              <Autocomplete
                options={allUsers}
                value={selectedUser}
                onChange={handleSelectChange}
                getOptionKey={(option) => option.id ?? ''}
                getOptionLabel={(option) => option.emailAddress ?? ''}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('reporting.select-user')}
                    placeholder={t('reporting.select-user')} />
                )}
              />
            </FormControl>
            {
              visibleRows().map((item) => {
                return (
                  <Box key={item.id}
                    sx={{ 
                      display: 'flex', 
                      width: '100%', 
                      minHeight: '60px', 
                      p: '12px',
                      alignItems: 'center',
                      justifyContent: 'left', 
                      mb: '12px', 
                      border: `solid 1px ${theme.palette.custom.gray[200]}`, 
                      borderRadius: '4px' }}>
                    <SvgIcon sx={{ width: '20px', mr: '24px' }}><BookClosed /></SvgIcon>
                    <Typography variant="textMD"
                      sx={{ display: 'flex', flex: 1, justifyContent: 'left', textAlign: 'left', overflow: 'auto' }}>
                      {t(item.displayName ?? '')} {t('reporting.created-at')} {item.createdAt} 
                      { (item.dateFrom && item.dateTo) && ` for ${item.dateFrom} - ${item.dateTo}` }
                      <br />
                      {item.storeName}
                    </Typography>
                    <Button size='lg'
                      onClick={() => downloadReport(item.name ?? '', item.id ?? 0)}
                      disabled={downloadReportId === item.id}
                      startIcon={<SvgIcon><DownloadCloud01 /></SvgIcon>}>
                      <Typography sx={{ display: { xs: 'none', md: 'block' } }}>{t('reporting.download')}</Typography>
                    </Button>
                  </Box>
                );
              })
            } {
              visibleRows().length > 0 ? (
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  maxPages={maxPages()}
                  breakpointForChangeDisplay={120000}
                />
              ) : (
                selectedUser !== null && t('noData')
              )
            }
          </>
      }
    </>
  );
};

export default UserReportsTab;